import { CaseState, CasePriority } from "../types/case";
import type { Case, UserInfo } from "../types/case";

// Map numeric state values from backend (matches CaseStatus.java)
const STATE_MAP: Record<number, CaseState> = {
  0: CaseState.Open,
  1: CaseState.InProgress,
  2: CaseState.AwaitingUpload,
  3: CaseState.PendingReview,
  4: CaseState.Escalated,
  5: CaseState.Rejected,
  6: CaseState.Closed,
  7: CaseState.Waived,
  8: CaseState.Resolved,
  // Compliance-specific states
  100: CaseState.ComplianceReview,
  101: CaseState.ComplianceApproved,
  102: CaseState.ComplianceRejected,
  103: CaseState.ComplianceEscalated,
  104: CaseState.ComplianceClosed,
  105: CaseState.ComplianceConfirmed,
  106: CaseState.ComplianceSvpInterview,
  107: CaseState.ComplianceManualExplanation,
  108: CaseState.ComplianceExempted,
};

// Default priority if not provided
const DEFAULT_PRIORITY = CasePriority.MEDIUM;

// Transform API comment to frontend Comment interface
export function transformApiComment(apiComment: any): any {
  // Create a proper user object with fallbacks
  const userId = apiComment.createdByUserId || "";
  const displayName =
    apiComment.createdByUserName ||
    apiComment.createdByUser?.displayName ||
    (userId
      ? userId
          .replace("-", " ")
          .replace(/\b\w/g, (l: string) => l.toUpperCase())
      : "Unknown User");

  return {
    id: apiComment.id,
    caseId: apiComment.caseId,
    content: apiComment.commentText || apiComment.content || "",
    commentText: apiComment.commentText || apiComment.content || "", // Keep original field for compatibility
    createdByUserId: userId,
    createdByUserName: apiComment.createdByUserName || displayName, // Keep original field for compatibility
    createdByUser: apiComment.createdByUser || {
      id: userId,
      displayName: displayName,
      firstName: displayName.split(" ")[0] || "Unknown",
      lastName: displayName.split(" ")[1] || "User",
      email: userId ? `${userId}@company.com` : "unknown@company.com",
    },
    createdAt: apiComment.createdAt,
    updatedAt: apiComment.updatedAt || apiComment.lastEditedAt,
    lastEditedAt: apiComment.updatedAt || apiComment.lastEditedAt, // Keep original field for compatibility
    lastEditedByUserId: apiComment.lastEditedByUserId,
    lastEditedByUserName: apiComment.lastEditedByUserName,
    editCount: apiComment.editCount || 0,
    // Detect if edited by comparing timestamps or use backend flag
    isEdited:
      apiComment.isEdited ||
      (apiComment.updatedAt && apiComment.updatedAt !== apiComment.createdAt) ||
      false,
  };
}

// Resolve display name from multiple sources with fallback chain
function resolveDisplayName(
  apiAttachment: any,
  getUserDisplayName?: (userId: string) => string,
  userId?: string,
): string {
  if (getUserDisplayName && userId) {
    const resolvedName = getUserDisplayName(userId);
    if (resolvedName && resolvedName !== userId) {
      return resolvedName;
    }
  }

  if (apiAttachment.uploadedByUser?.displayName) {
    return apiAttachment.uploadedByUser.displayName;
  }

  if (apiAttachment.uploadedByUserName) {
    return apiAttachment.uploadedByUserName;
  }

  return "Unknown User";
}

// Check if an API attachment represents a document group (multiple indicators)
function isDocumentGroupAttachment(apiAttachment: any): boolean {
  return (
    apiAttachment.isDocumentGroup === true ||
    apiAttachment.type === 1 ||
    apiAttachment.type === "DocumentGroup" ||
    (apiAttachment.mergedPdfPath &&
      apiAttachment.mergedPdfPath.includes("/groups/")) ||
    apiAttachment.hasMergedPdf === true
  );
}

// Build the common user fields shared by both attachment types
function buildUploadedByUser(userId: string, displayName: string) {
  return {
    uploadedByUserId: userId,
    uploadedByUserName: displayName,
    uploadedByUser: {
      id: userId,
      displayName: displayName,
      firstName: displayName.split(" ")[0] || "User",
      lastName: displayName.split(" ")[1] || "",
      email: userId ? `${userId}@company.com` : "unknown@company.com",
    },
  };
}

// Transform a document group attachment to frontend format
function transformDocumentGroupAttachment(
  apiAttachment: any,
  displayName: string,
  userId: string,
): any {
  // Parse fileHashes if it exists and is a string
  let fileHashes: any[] = [];
  if (apiAttachment.fileHashes) {
    try {
      fileHashes =
        typeof apiAttachment.fileHashes === "string"
          ? JSON.parse(apiAttachment.fileHashes)
          : apiAttachment.fileHashes;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      console.warn("Failed to parse fileHashes:", apiAttachment.fileHashes);
      fileHashes = [];
    }
  }

  const totalFileSize =
    fileHashes.length > 0
      ? fileHashes.reduce(
          (total: number, file: any) => total + (file.Size || 0),
          0,
        )
      : apiAttachment.totalSize || apiAttachment.fileSize || 0;

  const fileCount =
    fileHashes.length > 0 ? fileHashes.length : apiAttachment.fileCount || 0;

  return {
    id: apiAttachment.id,
    caseId: apiAttachment.caseId,
    name: apiAttachment.name,
    description: apiAttachment.description || "",
    type: "DocumentGroup",
    status: getAttachmentStatus(apiAttachment),
    tags: parseAttachmentTags(apiAttachment.tags),
    ...buildUploadedByUser(userId, displayName),
    uploadedAt: apiAttachment.uploadedAt,
    updatedAt: apiAttachment.lastModifiedAt || apiAttachment.uploadedAt,
    isActive: !apiAttachment.isDeleted,

    // Document Group specific fields
    groupId: apiAttachment.groupId || apiAttachment.id,
    fileCount: fileCount,
    totalFileSize: totalFileSize,
    mergedPdfS3Key: apiAttachment.mergedPdfPath,
    hasMergedPdf: apiAttachment.hasMergedPdf || false,
    isProcessing: apiAttachment.isProcessing || false,
    hasError: apiAttachment.hasError || false,
    version: apiAttachment.version || 1,
    contentHash: apiAttachment.contentHash,

    // Individual files within the group
    fileHashes: fileHashes,
    attachmentIds: [], // Not provided in new structure
    pageOrders: [], // Not provided in new structure
    isFinalized: apiAttachment.status === 2, // Status 2 seems to mean ready/finalized
    finalizedAt: apiAttachment.pdfGeneratedAt,
    finalizedByUserId: apiAttachment.pdfGeneratedBy,
  };
}

// Transform an individual attachment to frontend format
function transformIndividualAttachment(
  apiAttachment: any,
  displayName: string,
  userId: string,
): any {
  return {
    id: apiAttachment.id,
    caseId: apiAttachment.caseId,
    name: apiAttachment.fileName || apiAttachment.name,
    description: apiAttachment.description || "",
    type: "Individual",
    status: getAttachmentStatus(apiAttachment),
    tags: parseAttachmentTags(apiAttachment.tags),
    ...buildUploadedByUser(userId, displayName),
    uploadedAt: apiAttachment.uploadedAt,
    updatedAt: apiAttachment.lastModifiedAt || apiAttachment.uploadedAt,
    isActive: !apiAttachment.isDeleted,

    // Individual attachment specific fields
    fileName: apiAttachment.fileName || apiAttachment.name,
    originalFileName:
      apiAttachment.originalFileName ||
      apiAttachment.fileName ||
      apiAttachment.name,
    fileSize: apiAttachment.fileSize || apiAttachment.totalSize || 0,
    contentType:
      apiAttachment.contentType ||
      apiAttachment.primaryContentType ||
      apiAttachment.mimeType ||
      "",
    s3Key:
      apiAttachment.filePath ||
      apiAttachment.s3Key ||
      apiAttachment.files?.[0]?.filePath,
    version: apiAttachment.version || 1,
    contentHash: apiAttachment.contentHash,
    groupId: apiAttachment.groupId, // If part of a group
    pageOrder: apiAttachment.groupOrder,
  };
}

// Transform API attachment to frontend Attachment interface
export function transformApiAttachment(
  apiAttachment: any,
  getUserDisplayName?: (userId: string) => string,
): any {
  const userId = apiAttachment.uploadedByUserId || "";
  const displayName = resolveDisplayName(
    apiAttachment,
    getUserDisplayName,
    userId,
  );

  if (isDocumentGroupAttachment(apiAttachment)) {
    return transformDocumentGroupAttachment(apiAttachment, displayName, userId);
  }

  return transformIndividualAttachment(apiAttachment, displayName, userId);
}

// Helper function to determine attachment status
function getAttachmentStatus(apiAttachment: any): string {
  if (apiAttachment.hasError) return "Failed";
  if (apiAttachment.isProcessing) return "Processing";
  if (apiAttachment.status === 2 || apiAttachment.hasMergedPdf) return "Ready";
  if (apiAttachment.status === 1) return "Uploading";
  return "Ready";
}

// Helper function to parse tags
function parseAttachmentTags(tags: any): string[] {
  if (!tags) return [];
  if (Array.isArray(tags)) {
    // Handle array of strings or array with JSON strings
    return tags.flatMap((tag) => {
      if (typeof tag === "string") {
        try {
          // Try to parse as JSON first
          const parsed = JSON.parse(tag);
          return Array.isArray(parsed) ? parsed : [tag];
        } catch {
          // If not JSON, return as is
          return [tag];
        }
      }
      return [String(tag)];
    });
  }
  if (typeof tags === "string") {
    try {
      const parsed = JSON.parse(tags);
      return Array.isArray(parsed) ? parsed : [tags];
    } catch {
      return [tags];
    }
  }
  return [];
}

// Transform API response to match expected Case interface
export function transformApiCase(
  apiCase: any,
  getUserDisplayName?: (userId: string) => string,
): Case {
  // Extract assigned users from userAssignments array
  const assignedUsers: UserInfo[] =
    apiCase.userAssignments?.map((assignment: any) => ({
      id: assignment.assignedUserId,
      email: assignment.assignedUserId + "@company.com", // Placeholder email
      firstName: assignment.assignedUserId.split("-")[0] || "User",
      lastName: assignment.assignedUserId.split("-")[1] || "Name",
      displayName: assignment.assignedUserId
        .replace("-", " ")
        .replace(/\b\w/g, (l: string) => l.toUpperCase()),
      isActive: assignment.isActive,
      role: "User", // Default role
    })) || [];

  return {
    id: apiCase.id,
    caseNumber: apiCase.caseNumber, // Case number from API
    title: apiCase.title,
    description: apiCase.description,
    state: STATE_MAP[apiCase.state] || CaseState.Open,
    priority: apiCase.priority || DEFAULT_PRIORITY, // Use default if not provided
    severity: apiCase.severity, // Map severity from API
    dueDate: apiCase.dueDate,
    createdAt: apiCase.createdAt,
    updatedAt: apiCase.updatedAt || apiCase.createdAt, // Use createdAt if updatedAt is missing
    complianceNodeId: apiCase.complianceNodeId,
    companyId: apiCase.companyId,
    createdByUserId: apiCase.createdByUserId || "system",
    ruleId: apiCase.ruleId, // Rule ID for fetching rule details

    // User audit information (required by backend)
    createdBy: apiCase.createdBy || {
      userId: apiCase.createdByUserId || "system",
      username: apiCase.createdByUsername || "system",
      timestamp: apiCase.createdAt,
    },
    updatedBy: apiCase.updatedBy || {
      userId: apiCase.updatedByUserId || apiCase.createdByUserId || "system",
      username:
        apiCase.updatedByUsername || apiCase.createdByUsername || "system",
      timestamp: apiCase.updatedAt || apiCase.createdAt,
    },

    // Compliance rule information from API
    complianceNodeTitle: apiCase.complianceNodeTitle,
    complianceNodeDescription: apiCase.complianceNodeDescription,
    sourceRuleId: apiCase.sourceRuleId,
    sourceChapterId: apiCase.sourceChapterId,
    rule: apiCase.rule, // Full rule object from backend
    chapter: apiCase.chapter, // Chapter name from rule hierarchy
    documents: apiCase.documents, // Attached documents
    documentIds: apiCase.documentIds, // Document IDs
    roleId: apiCase.roleId, // Role ID associated with the case

    // Manual case creation fields (ALH-194)
    creationType: apiCase.creationType, // 'MANUAL' or 'AUTOMATED'
    resolutionInstructions: apiCase.resolutionInstructions, // How to resolve failure
    nonComplianceReason: apiCase.nonComplianceReason ?? null,

    // Structured description (camelCase top-level keys, snake_case interior).
    // Populated when GET /cases/{id}?structuredDescription=true is requested.
    // MUST be passed through verbatim — backend already returns camelCase here.
    descriptionFormat: apiCase.descriptionFormat ?? null,
    complianceDescription: apiCase.complianceDescription ?? null,

    // Count fields from API (ALH-177: used for engagement stats in CaseCard)
    // ALH-440: Use document/attachment arrays as fallback when explicit count fields are absent
    commentCount: apiCase.commentCount ?? apiCase.comments?.length ?? 0,
    attachmentCount:
      apiCase.attachmentCount ?? apiCase.attachments?.length ?? 0,
    linkedRuleDocumentCount:
      apiCase.linkedRuleDocumentCount ??
      apiCase.linkedDocuments?.length ??
      apiCase.documentIds?.length ??
      0,
    caseDocumentCount:
      apiCase.caseDocumentCount ??
      apiCase.caseDocuments?.length ??
      apiCase.documents?.length ??
      0,

    // Transform userAssignments to assignedUsers
    userAssignments: apiCase.userAssignments || [],
    assignedUsers,
    currentAssignees: assignedUsers,

    // assignedTo field from API (primary assignment field)
    assignedTo: apiCase.assignedTo || null,

    // Other arrays - comments are now fetched separately via dedicated API
    comments: [], // Comments are fetched separately via useCaseComments hook
    attachments: (apiCase.attachments || []).map((attachment: any) =>
      transformApiAttachment(attachment, getUserDisplayName),
    ),
    actionLogs: apiCase.actionLogs || [],

    // Optional fields
    tags: apiCase.tags || [],
    regulatoryBody: apiCase.regulatoryBody,
    complianceFramework: apiCase.complianceFramework,
    potentialFine: apiCase.potentialFine,
  };
}

// Transform array of API cases
export function transformApiCases(
  apiCases: any[],
  getUserDisplayName?: (userId: string) => string,
): Case[] {
  if (!Array.isArray(apiCases)) {
    return [];
  }

  return apiCases.map((apiCase) =>
    transformApiCase(apiCase, getUserDisplayName),
  );
}

// Transform API response to CaseInboxResponse format
export function transformCaseInboxResponse(
  apiResponse: any,
  getUserDisplayName?: (userId: string) => string,
): {
  cases: Case[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
} {
  // If apiResponse is an array (direct API response), create pagination info
  if (Array.isArray(apiResponse)) {
    const cases = transformApiCases(apiResponse, getUserDisplayName);
    return {
      cases,
      totalCount: cases.length,
      pageNumber: 1,
      pageSize: cases.length,
      totalPages: 1,
      hasNextPage: false,
      hasPreviousPage: false,
    };
  }

  // If apiResponse has pagination structure
  return {
    cases: transformApiCases(
      apiResponse.items || apiResponse.cases || [],
      getUserDisplayName,
    ),
    totalCount: apiResponse.totalCount || 0,
    pageNumber: apiResponse.pageNumber || 1,
    pageSize: apiResponse.pageSize || 20,
    totalPages: apiResponse.totalPages || 1,
    hasNextPage: apiResponse.hasNextPage || false,
    hasPreviousPage: apiResponse.hasPreviousPage || false,
  };
}
